import React from "react";
import { Box, Tooltip } from "@mui/material";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import { Machine } from "../models/models";
import { dt } from "../utils/dt";
import { getMachineDisplayName, getMachineName, getBrandDisplayName, getProductionYearDisplay, getMachineTypeLabel } from "../../../shared/utils/machineUtils";
import { getReferenceLabel } from "../../../shared/utils/machineReferenceLabels";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ImageGallery, useGalleryImages } from "../../../shared/components/gallery";
import { getUiLocale } from "../../../shared/utils/relativeTime";

// =====================================================================
//  GROUP PILL COLORS (same as RedesignedMachineCard)
// =====================================================================

const GROUP_COLORS = [
  "#3498DB", "#8E44AD", "#27AE60", "#E67E22", "#E74C3C",
  "#1ABC9C", "#2980B9", "#9B59B6", "#F39C12", "#16A085",
];

const getGroupColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return GROUP_COLORS[Math.abs(hash) % GROUP_COLORS.length];
};

// =====================================================================
//  HEALTH INDICATOR
// =====================================================================

const getHealthColor = (openServiceReportsCount: number): string => {
  if (openServiceReportsCount === 0) return dt.machinery.health.green;
  if (openServiceReportsCount <= 2) return dt.machinery.health.yellow;
  return dt.machinery.health.red;
};

// =====================================================================
//  COMPONENT
// =====================================================================

interface ProfileSidebarCardProps {
  machine: Machine;
}

const ProfileSidebarCard = ({ machine }: ProfileSidebarCardProps) => {
  const { translate } = useTranslation("myMachinery");

  const displayName = getMachineDisplayName(machine);
  const subtitle = getMachineName(machine);
  const brandName = getBrandDisplayName(machine);
  const yearDisplay = getProductionYearDisplay(machine);
  const isDecommissioned = machine.isDecommissioned === true;

  const galleryImages = useGalleryImages({
    type: "machine",
    image: machine?.image?.content || null,
    catalogImageUrl: machine?.catalogModel?.imageStandardUrl || machine?.catalogModel?.imageUrl || null,
    catalogImages: machine?.catalogModel?.images || [],
    files: machine?.files?.map(f => ({ name: f.name, content: f.content })) || [],
  });

  const groupNames = machine.groupNames || [];

  const healthColor = getHealthColor(machine.openServiceReportsCount);
  const healthTooltip =
    machine.openServiceReportsCount === 0
      ? translate("healthNoIssues")
      : translate("healthOpenReports", { count: machine.openServiceReportsCount });

  return (
    <Box
      sx={{
        background: dt.machinery.card.bg,
        border: isDecommissioned
          ? dt.decommissioned.card.borderStyle
          : dt.machinery.card.border,
        borderRadius: dt.machinery.card.radius,
        padding: "20px",
        boxShadow: dt.machinery.card.shadow,
        fontFamily: dt.font,
        opacity: isDecommissioned ? dt.decommissioned.card.opacity : 1,
        animation: "fadeLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
      }}
    >
      {/* Health dot (top-right corner) */}
      {!isDecommissioned && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "8px" }}>
          <Tooltip title={healthTooltip} arrow placement="top">
            <Box
              sx={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: healthColor,
                boxShadow: `0 0 6px ${healthColor}80`,
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Decommissioned badge */}
      {isDecommissioned && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "8px" }}>
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.decommissioned.badge.text,
              backgroundColor: dt.decommissioned.badge.bg,
              border: `1px solid ${dt.decommissioned.badge.border}`,
              borderRadius: "10px",
              padding: "2px 8px",
              lineHeight: "16px",
            }}
          >
            {translate("decommissioned")}
          </Box>
        </Box>
      )}

      {/* Machine image gallery */}
      <Box
        sx={{
          mx: "auto",
          mb: "16px",
          display: "flex",
          justifyContent: "center",
          filter: isDecommissioned
            ? dt.decommissioned.card.imageFilter
            : "none",
          transition: "filter 0.2s ease",
        }}
      >
        <ImageGallery images={galleryImages} variant="profile" />
      </Box>

      {/* Machine name */}
      <Box
        sx={{
          fontSize: "18px",
          fontWeight: 600,
          fontFamily: dt.font,
          color: dt.text.primary,
          textAlign: "center",
          lineHeight: 1.3,
          wordBreak: "break-word",
        }}
      >
        {displayName}
      </Box>

      {/* Subtitle (manufacturer + model description) */}
      {subtitle && (
        <Box
          sx={{
            fontSize: "14px",
            fontWeight: 400,
            fontFamily: dt.font,
            color: dt.text.secondary,
            textAlign: "center",
            mt: "4px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subtitle}
        </Box>
      )}

      {/* Brand name with hierarchy */}
      {brandName && (
        <Box
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            fontFamily: dt.font,
            color: dt.text.secondary,
            textAlign: "center",
            mt: "2px",
          }}
        >
          {machine.catalogModel?.manufacturer?.parentManufacturerName ? (
            <>
              <Box
                component="span"
                sx={{ fontSize: "12px", color: dt.text.tertiary }}
              >
                {machine.catalogModel.manufacturer.parentManufacturerName}
              </Box>
              <Box component="span" sx={{ fontSize: "11px", color: dt.text.tertiary, mx: "4px" }}>
                &gt;
              </Box>
              <Box component="span" sx={{ fontWeight: 500, color: dt.text.secondary }}>
                {machine.catalogModel.manufacturer.name}
              </Box>
            </>
          ) : (
            brandName
          )}
        </Box>
      )}

      {/* Spec details section */}
      <Box
        sx={{
          mt: "16px",
          pt: "16px",
          borderTop: `1px dashed ${dt.border.light}`,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {/* Serial Number */}
        {machine.serialNumber && (
          <SpecLine
            label={translate("sn")}
            value={machine.serialNumber}
            mono
          />
        )}

        {/* Machine Number */}
        {machine.internalNumber && (
          <SpecLine
            label={translate("machineNo")}
            value={machine.internalNumber}
            mono
          />
        )}

        {/* Brand */}
        {brandName && (
          <SpecLine
            label={translate("brand")}
            value={brandName}
          />
        )}

        {/* Type */}
        {machine.type?.name && (
          <SpecLine
            label={translate("type")}
            value={getMachineTypeLabel(machine.type).toUpperCase()}
          />
        )}

        {/* Location -- highlighted row with blue left accent */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            borderLeft: `3px solid ${dt.blue}`,
            paddingLeft: "8px",
            marginLeft: "-2px",
            borderRadius: "0 4px 4px 0",
            backgroundColor: dt.blueSoft,
            padding: "6px 8px 6px 10px",
          }}
        >
          <Box
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              fontFamily: dt.font,
              color: dt.text.tertiary,
              whiteSpace: "nowrap",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {translate("locationLabel")}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              minWidth: 0,
            }}
          >
            <PlaceOutlined
              sx={{
                fontSize: "14px",
                color: machine.locationName ? dt.blue : dt.text.tertiary,
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: machine.locationName ? 500 : 400,
                fontFamily: dt.font,
                color: machine.locationName ? dt.text.primary : dt.text.tertiary,
                fontStyle: machine.locationName ? "normal" : "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                textAlign: "right",
              }}
            >
              {machine.locationName
                ? machine.parentLocationName
                  ? `${machine.parentLocationName} > ${machine.locationName}`
                  : machine.locationName
                : translate("locationNotSet")}
            </Box>
          </Box>
        </Box>

        {/* Control System */}
        {machine.controlSystemBrand?.name && (
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                fontFamily: dt.font,
                color: dt.text.tertiary,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {translate("system")}
            </Box>
            <Box
              sx={{
                minWidth: 0,
                textAlign: "right",
              }}
            >
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  fontFamily: dt.font,
                  color: dt.text.primary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getReferenceLabel("brand", machine.controlSystemBrand)}
              </Box>
              {machine.system && (
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    fontFamily: dt.font,
                    color: dt.text.tertiary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    mt: "1px",
                  }}
                >
                  {machine.system}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Production Year */}
        {yearDisplay && (
          <SpecLine
            label={translate("productionYear")}
            value={yearDisplay}
          />
        )}

        {/* Phase 7F: Taper Type */}
        {machine.taperType?.name && (
          <Tooltip
            title={
              machine.taperType.standard
                ? `${machine.taperType.name} - ${machine.taperType.standard}`
                : machine.taperType.name
            }
            arrow
            placement="left"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 400,
                  fontFamily: dt.font,
                  color: dt.text.tertiary,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {translate("taperType")}
              </Box>
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  fontFamily: dt.fontMono,
                  color: "#8E44AD",
                  backgroundColor: "rgba(142, 68, 173, 0.08)",
                  borderRadius: "10px",
                  padding: "2px 8px",
                  lineHeight: "16px",
                  whiteSpace: "nowrap",
                }}
              >
                {getReferenceLabel("taper", machine.taperType)}
              </Box>
            </Box>
          </Tooltip>
        )}

        {/* Phase 7F: Key specs from catalog */}
        {machine.maxSpindleRpm && (
          <SpecLine
            label={translate("maxSpindleRpm")}
            value={machine.maxSpindleRpm.toLocaleString(getUiLocale()) + " RPM"}
            mono
          />
        )}
        {machine.spindleHp && (
          <SpecLine
            label={translate("spindleHp")}
            value={machine.spindleHp + " HP"}
            mono
          />
        )}
        {machine.toolCapacity && (
          <SpecLine
            label={translate("toolCapacity")}
            value={machine.toolCapacity.toString()}
            mono
          />
        )}
      </Box>

      {/* Group pills */}
      {groupNames.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "5px",
            mt: "14px",
            justifyContent: "center",
          }}
        >
          {groupNames.map((name: string) => {
            const color = getGroupColor(name);
            return (
              <Box
                key={name}
                sx={{
                  fontSize: dt.machinery.groupPill.fontSize,
                  fontWeight: 500,
                  fontFamily: dt.font,
                  color: color,
                  backgroundColor: `${color}14`,
                  borderRadius: dt.machinery.groupPill.radius,
                  padding: `${dt.machinery.groupPill.py} ${dt.machinery.groupPill.px}`,
                  whiteSpace: "nowrap",
                  lineHeight: "16px",
                }}
              >
                {name}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Catalog badge */}
      {machine.catalogModelId && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: "12px",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "11px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.blue,
              backgroundColor: dt.blueSoft,
              border: `1px solid ${dt.blueGlow}`,
              borderRadius: "10px",
              padding: "2px 10px",
              lineHeight: "16px",
            }}
          >
            <PrecisionManufacturingOutlinedIcon sx={{ fontSize: 13 }} />
            {translate("catalogModel")}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// =====================================================================
//  SPEC LINE HELPER
// =====================================================================

interface SpecLineProps {
  label: string;
  value: string;
  mono?: boolean;
}

const SpecLine = ({ label, value, mono }: SpecLineProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: "8px",
    }}
  >
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: 400,
        fontFamily: dt.font,
        color: dt.text.tertiary,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "13px",
        fontWeight: 500,
        fontFamily: mono ? dt.fontMono : dt.font,
        color: dt.text.primary,
        letterSpacing: mono ? "0.3px" : undefined,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        textAlign: "right",
      }}
    >
      {value}
    </Box>
  </Box>
);

export default ProfileSidebarCard;
